import { useParams, Link, Navigate } from "react-router-dom";
import { MapPin, Phone, Check, ArrowRight, ArrowLeft, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, stagger, item } from "@/components/Reveal";
import { FAQ } from "@/components/FAQ";
import { getLocation, LOCATIONS } from "@/data/locations";
import { RESIDENTIAL, CONTACT } from "@/data/site";
import { useLeadModal } from "@/context/LeadModalProvider";
import { useSEO, faqSchema, breadcrumbSchema, serviceSchema } from "@/lib/seo";

export default function ServiceAreaDetail() {
  const { slug } = useParams();
  const { openBooking, openQuote } = useLeadModal();
  const loc = getLocation(slug);
  const fullName = loc ? `${loc.name}, ${loc.state}` : "";
  const mapSrc = loc ? `https://www.google.com/maps?q=${encodeURIComponent(loc.q)}&z=12&output=embed` : "";
  const others = loc ? LOCATIONS.filter((l) => l.slug !== slug).slice(0, 8) : [];

  useSEO(loc ? {
    title: loc.title,
    description: loc.metaDescription,
    keywords: loc.keywords,
    path: `/service-areas/${slug}`,
    jsonLd: [
      serviceSchema({
        name: `House & Commercial Cleaning in ${fullName}`,
        description: loc.metaDescription,
        path: `/service-areas/${slug}`,
        areaServed: [{ "@type": "City", name: fullName }],
      }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Service Areas", path: "/service-areas" },
        { name: fullName, path: `/service-areas/${slug}` },
      ]),
      faqSchema(loc.faqs),
    ],
  } : { title: "Service Areas | CJB Clean", path: "/service-areas" });

  if (!loc) return <Navigate to="/service-areas" replace />;

  return (
    <div data-testid="service-area-detail-page">
      {/* HERO */}
      <section className="pt-36 pb-14 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_82%_12%,rgba(95,165,68,0.10),transparent_45%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Link to="/service-areas" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-leaf transition-colors mb-6" data-testid="back-to-areas">
              <ArrowLeft className="w-4 h-4" /> All Service Areas
            </Link>
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4 flex items-center gap-2"><MapPin className="w-4 h-4" /> {fullName}</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-navy leading-[0.95]">{loc.h1}</h1>
            <div className="mt-6 space-y-4 text-lg text-muted-foreground leading-relaxed">
              {loc.intro.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button onClick={openQuote} data-testid="cta-quote-btn" className="group px-7 py-4 rounded-full bg-leaf text-white font-medium flex items-center justify-center gap-2 hover:scale-105 transition-transform">
                <Phone className="w-4 h-4" /> Get Instant Quote
              </button>
              <button onClick={openBooking} data-testid="cta-book-btn" className="px-7 py-4 rounded-full font-medium border border-navy/25 text-navy hover:bg-navy hover:text-white transition-all duration-200 hover:scale-105">
                Book Online
              </button>
            </div>
          </div>
          <div className="relative rounded-[2rem] overflow-hidden border border-border shadow-xl aspect-[4/3] lg:aspect-square">
            <iframe title={`Map of ${fullName}`} data-testid="area-map" src={mapSrc} width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen className="absolute inset-0 w-full h-full" />
          </div>
        </div>
      </section>

      {/* NEIGHBORHOODS */}
      <section className="py-14 bg-muted/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><h2 className="font-heading text-2xl sm:text-3xl font-semibold text-navy mb-6">Neighborhoods we serve in {loc.name}</h2></Reveal>
          <div className="flex flex-wrap gap-2">
            {loc.neighborhoods.map((n) => (
              <span key={n} data-testid={`neighborhood-${n}`} className="flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-white border border-border text-navy">
                <Check className="w-4 h-4 text-leaf" /> {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES IN THIS AREA */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><h2 className="font-heading text-2xl sm:text-3xl font-semibold text-navy mb-2">Cleaning services in {loc.name}</h2></Reveal>
          <Reveal delay={0.05}><p className="text-muted-foreground mb-8 max-w-2xl">All of our residential services are available in {fullName}, with Green Cleaning included on every visit.</p></Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {RESIDENTIAL.map((s) => (
              <motion.div key={s.slug} variants={item}>
                <Link to={`/services/${s.slug}`} data-testid={`area-service-${s.slug}`}
                  className="group block bg-white rounded-3xl p-6 border border-border hover:border-leaf/40 hover:-translate-y-1 transition-all duration-300 h-full">
                  <h3 className="font-heading text-lg font-semibold text-navy group-hover:text-leaf transition-colors">{s.name}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{s.blurb}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-leaf mt-4">Learn more <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              </motion.div>
            ))}
            <motion.div variants={item}>
              <Link to="/services/commercial-cleaning" className="group block bg-navy text-white rounded-3xl p-6 hover:-translate-y-1 transition-all duration-300 h-full">
                <h3 className="font-heading text-lg font-semibold">Commercial Cleaning</h3>
                <p className="text-sm text-white/70 mt-2">Offices, multi-family, community facilities & retail in {loc.name}.</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-leaf mt-4">Learn more <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* GREEN NOTE + nearby areas */}
      <section className="py-14 bg-muted/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-4"><Leaf className="w-6 h-6 text-leaf" /><h2 className="font-heading text-xl font-semibold text-navy">Nearby areas we also serve</h2></div>
          <div className="flex flex-wrap gap-2">
            {others.map((l) => (
              <Link key={l.slug} to={`/service-areas/${l.slug}`} data-testid={`nearby-${l.slug}`} className="px-4 py-2 rounded-full text-sm bg-white border border-border text-navy hover:border-leaf transition-colors">{l.name}, {l.state}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="booking-cta">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Leaf className="w-8 h-8 text-leaf mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-navy mb-4">Book Your Cleaning in {loc.name}</h2>
          <p className="text-lg text-muted-foreground mb-8">Ready to schedule? Book online in a few taps — or get an instant price estimate first.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <button onClick={openQuote} data-testid="cta-quote-btn"
              className="group px-7 py-4 rounded-full bg-white border border-navy/25 text-navy font-medium flex items-center justify-center gap-2 hover:scale-105 transition-transform">
              <Phone className="w-4 h-4" /> Get Instant Quote
            </button>
            <button onClick={openBooking} data-testid="cta-book-btn"
              className="px-7 py-4 rounded-full font-medium bg-leaf text-white hover:scale-105 transition-transform duration-200">
              Book Online
            </button>
          </div>
        </div>
      </section>
      <FAQ items={loc.faqs} title={`${fullName} — Frequently Asked Questions`} />
    </div>
  );
}
